import { apiClient } from './api'
import { track } from '../utils/analytics'

/**
 * Sprint 32 — team-invitation service.
 *
 * Wraps the backend `/api/v1/invitations` surface (create, list, accept,
 * resend, cancel). Mirrors the structure of `services/guests.ts`. The accept
 * route is public (token-based, no auth header); the rest require `user:*`.
 */

export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'cancelled'

export interface UserInvitation {
  id: number
  email: string
  full_name?: string | null
  organization_id?: number | null
  invited_by: number
  status: InvitationStatus
  expires_at: string
  created_at: string
  /** Create only: false when the email didn't go out (see email_warning). */
  email_sent?: boolean | null
  email_warning?: string | null
}

export interface InvitationCreateInput {
  email: string
  full_name?: string
  role_ids?: number[]
  message?: string
  organization_id?: number
  /** 'viewer' = read-only and free while the server's FREE_VIEWER_SEATS is on. */
  access?: 'member' | 'viewer'
}

export const userInvitationsService = {
  create: async (input: InvitationCreateInput): Promise<UserInvitation> => {
    const { data } = await apiClient.post('/api/v1/invitations/', input)
    track('invite_sent')
    return data
  },

  list: async (status?: InvitationStatus): Promise<UserInvitation[]> => {
    const { data } = await apiClient.get('/api/v1/invitations/', {
      params: status ? { status } : undefined,
    })
    return data
  },

  resend: async (id: number): Promise<{ sent: boolean }> => {
    const { data } = await apiClient.post(`/api/v1/invitations/${id}/resend`)
    return data
  },

  cancel: async (id: number): Promise<void> => {
    await apiClient.delete(`/api/v1/invitations/${id}`)
  },

  // Public — reached from the emailed link (/accept-invitation?token=…).
  // The password travels in the body, never the query string.
  accept: async (token: string, password: string): Promise<{ message: string; user_id: number; email: string }> => {
    const { data } = await apiClient.post(`/api/v1/invitations/accept/${token}`, { password })
    track('invite_accepted')
    return data
  },
}
